import { TimeLog, Employee, TimesheetEntry, PayrollEntry, EditableTimeSegment, TimecardSummaryData, EmployeeSummary, PayrollFrequency, OvertimeRule, DayOfWeek, TimeSettings, TimeRounding, TimecardDetailData, PayrollSettings, LeaveEntry, LeaveCategory, LeaveCategoryCalculation, NameDisplayFormat, Activity, ActivityCalculation, Schedules, Shift, DisplayGroup } from '../shared/types.js';
export * from '../shared/utils.js';
import { correctDateForTimezone, calculatePeriodDates, toDateInputString, calculatePreviousPeriodStartDate, getTodaysShiftForEmployee, formatEmployeeName } from '../shared/utils.js';


export function formatDuration(ms: number, showSeconds = true): string {
    if (ms < 0) ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (showSeconds) {
        const seconds = totalSeconds % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function formatDecimalHoursToHHMM(decimalHours: number): string {
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours)}:${String(minutes).padStart(2, '0')}`;
}

export function formatCurrency(amount: number | undefined): string {
    if (amount === undefined || typeof amount !== 'number') {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(0);
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount);
}

export type DateRangePreset = 'custom' | 'today' | 'this_period' | 'this_week' | 'this_month' | 'this_year' | 'yesterday' | 'last_period' | 'last_week' | 'last_month' | 'last_year';

export const DATE_RANGE_PRESET_OPTIONS: Record<DateRangePreset, string> = {
    yesterday: 'Yesterday',
    today: 'Today',
    last_period: 'Last Period',
    this_period: 'Current Period',
    last_week: 'Last Week',
    this_week: 'This Week',
    last_month: 'Last Month',
    this_month: 'This Month',
    last_year: 'Last Year',
    this_year: 'This Year',
    custom: 'Custom',
};

export const DATE_RANGE_PRESET_ORDER: DateRangePreset[] = [
    'yesterday',
    'today',
    'last_period',
    'this_period',
    'last_week',
    'this_week',
    'last_month',
    'this_month',
    'last_year',
    'this_year',
    'custom',
];

export function getDateRangeForPreset(preset: DateRangePreset, settings: PayrollSettings): { start: string, end: string } {
    const today = new Date();
    switch (preset) {
        case 'today': {
            return { start: toDateInputString(today), end: toDateInputString(today) };
        }
        case 'yesterday': {
            const yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            return { start: toDateInputString(yesterday), end: toDateInputString(yesterday) };
        }
        case 'this_week': {
            const dayOfWeek = today.getDay();
            const startDate = new Date(today);
            startDate.setDate(today.getDate() - dayOfWeek);
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6);
            return { start: toDateInputString(startDate), end: toDateInputString(endDate) };
        }
        case 'last_week': {
            const startDate = new Date(today);
            startDate.setDate(today.getDate() - today.getDay() - 7);
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6);
            return { start: toDateInputString(startDate), end: toDateInputString(endDate) };
        }
        case 'this_month': {
            const year = today.getFullYear();
            const month = today.getMonth();
            const startDate = new Date(year, month, 1);
            const endDate = new Date(year, month + 1, 0);
            return { start: toDateInputString(startDate), end: toDateInputString(endDate) };
        }
        case 'last_month': {
            const year = today.getFullYear();
            const month = today.getMonth();
            const startDate = new Date(year, month - 1, 1);
            const endDate = new Date(year, month, 0);
            return { start: toDateInputString(startDate), end: toDateInputString(endDate) };
        }
        case 'this_year': {
            const year = today.getFullYear();
            const startDate = new Date(year, 0, 1);
            const endDate = new Date(year, 11, 31);
            return { start: toDateInputString(startDate), end: toDateInputString(endDate) };
        }
        case 'last_year': {
            const year = today.getFullYear() - 1;
            const startDate = new Date(year, 0, 1);
            const endDate = new Date(year, 11, 31);
            return { start: toDateInputString(startDate), end: toDateInputString(endDate) };
        }
        case 'this_period': {
            const { start, end } = calculatePeriodDates(settings);
            return { start: toDateInputString(start), end: toDateInputString(end) };
        }
        case 'last_period': {
            const { start: configuredStart } = calculatePeriodDates(settings);
            const dayBefore = new Date(configuredStart);
            dayBefore.setDate(configuredStart.getDate() - 1);
            let lastPeriodStartDate: Date;
            switch (settings.frequency) {
                case PayrollFrequency.WEEKLY:
                    lastPeriodStartDate = new Date(dayBefore);
                    lastPeriodStartDate.setDate(dayBefore.getDate() - 6);
                    break;
                case PayrollFrequency.BIWEEKLY:
                    lastPeriodStartDate = new Date(dayBefore);
                    lastPeriodStartDate.setDate(dayBefore.getDate() - 13);
                    break;
                case PayrollFrequency.SEMI_MONTHLY:
                    if (dayBefore.getDate() > 15) {
                        lastPeriodStartDate = new Date(dayBefore.getFullYear(), dayBefore.getMonth(), 16);
                    } else {
                        lastPeriodStartDate = new Date(dayBefore.getFullYear(), dayBefore.getMonth(), 1);
                    }
                    break;
                case PayrollFrequency.MONTHLY:
                     lastPeriodStartDate = new Date(dayBefore.getFullYear(), dayBefore.getMonth(), 1);
                     break;
                case PayrollFrequency.EMPLOYEE_DEFINED:
                    if (settings.endDate) {
                        const currentStart = correctDateForTimezone(settings.startDate);
                        const currentEnd = correctDateForTimezone(settings.endDate);
                        const durationMs = currentEnd.getTime() - currentStart.getTime();
                        const lastPeriodEnd = new Date(currentStart);
                        lastPeriodEnd.setDate(currentStart.getDate() - 1);
                        const lastPeriodStart = new Date(lastPeriodEnd.getTime() - durationMs);
                        return { start: toDateInputString(lastPeriodStart), end: toDateInputString(lastPeriodEnd) };
                    }
                    return { start: toDateInputString(configuredStart), end: toDateInputString(configuredStart) };
                default:
                    lastPeriodStartDate = configuredStart;
            }
             const { start, end } = calculatePeriodDates({...settings, startDate: toDateInputString(lastPeriodStartDate)});
             return { start: toDateInputString(start), end: toDateInputString(end) };
        }
        default:
            return { start: toDateInputString(today), end: toDateInputString(today) };
    }
}

export function processTimesheets(timeLogs: TimeLog[], employees: Employee[], startDate: Date, endDate: Date): TimesheetEntry[] {
    return []; 
}

export function downloadCSV(data: TimesheetEntry[]) {
    const headers = ['Date', 'Employee Name', 'Clock In', 'Clock Out', 'Break Duration (HH:MM:SS)', 'Total Work Duration (HH:MM:SS)'];
    const rows = data.map(entry => [
        entry.date,
        entry.employeeName,
        entry.clockInTime ? new Date(entry.clockInTime).toLocaleTimeString() : 'N/A',
        entry.clockOutTime ? new Date(entry.clockOutTime).toLocaleTimeString() : 'N/A',
        formatDuration(entry.breakDuration),
        formatDuration(entry.totalWorkDuration)
    ]);
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += headers.join(",") + "\r\n";
    rows.forEach(rowArray => {
        let row = rowArray.join(",");
        csvContent += row + "\r\n";
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `timesheet_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function downloadPayrollCSV(data: PayrollEntry[]) {
    const headers = ['Employee Name', 'Total Hours'];
    const rows = data.map(entry => [
        entry.employeeName,
        entry.totalHours.toFixed(2),
    ]);
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += headers.join(",") + "\r\n";
    rows.forEach(rowArray => {
        let row = rowArray.join(",");
        csvContent += row + "\r\n";
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `payroll_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function getWorkWeek(d: Date, workweekStartsWith: DayOfWeek = DayOfWeek.Sunday): { start: Date, end: Date } {
    const date = new Date(d);
    const dayMap: Record<DayOfWeek, number> = {
        [DayOfWeek.Sunday]: 0, [DayOfWeek.Monday]: 1, [DayOfWeek.Tuesday]: 2, [DayOfWeek.Wednesday]: 3, 
        [DayOfWeek.Thursday]: 4, [DayOfWeek.Friday]: 5, [DayOfWeek.Saturday]: 6,
    };
    const startDayNum = dayMap[workweekStartsWith];
    const currentDayNum = date.getDay();
    let diff = currentDayNum - startDayNum;
    if (diff < 0) {
        diff += 7;
    }
    const start = new Date(date);
    start.setDate(date.getDate() - diff);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
}

function roundHours(hours: number, rounding: TimeRounding): number {
    if (rounding === TimeRounding.TENTH_HOUR) {
        return Math.round(hours * 10) / 10;
    }
    if (rounding === TimeRounding.QUARTER_HOUR) {
        return Math.round(hours * 4) / 4;
    }
    return hours;
}

export function generateTimecardDetailData(
    employee: Employee,
    timeLogs: TimeLog[],
    leaveEntries: LeaveEntry[],
    leaveCategories: LeaveCategory[],
    activities: Activity[],
    startDate: Date,
    endDate: Date,
    overtimeRules: OvertimeRule[],
    timeSettings: TimeSettings
): TimecardDetailData {
    const employeeTimeLogs = timeLogs
        .filter(log => log.employeeId === employee.emp_number && log.startTime >= startDate.getTime() && log.startTime <= endDate.getTime() && log.endTime)
        .sort((a, b) => a.startTime - b.startTime);
    const processedLogs: TimeLog[] = [];
    if (!timeSettings.includePastMidnight) {
        employeeTimeLogs.forEach(log => {
            const start = new Date(log.startTime);
            const end = new Date(log.endTime!);
            if (start.toDateString() !== end.toDateString()) {
                const midnight = new Date(start);
                midnight.setHours(24, 0, 0, 0);
                processedLogs.push({
                    ...log,
                    endTime: midnight.getTime() - 1,
                });
                processedLogs.push({
                    ...log,
                    id: `${log.id}-split`,
                    startTime: midnight.getTime(),
                });
            } else {
                processedLogs.push(log);
            }
        });
    } else {
        processedLogs.push(...employeeTimeLogs);
    }
    const employeeLeaveDays: { date: Date; category: LeaveCategory; hours: number }[] = [];
    const employeeLeaveEntries = leaveEntries.filter(le => le.employeeIds.includes(employee.id));
    for(const leave of employeeLeaveEntries) {
        const category = leaveCategories.find(c => c.id === leave.leaveCategoryId);
        if(!category) continue;
        let currentDate = new Date(leave.startDate);
        while(currentDate.getTime() <= leave.endDate) {
            const dayOfWeek = currentDate.getDay();
            const isOrangeHRMLeave = leave.id.startsWith('ohrm_');
            const skipSaturday = leave.skipSaturday ?? true;
            const skipSunday = leave.skipSunday ?? true;
            const isSkipped = !isOrangeHRMLeave && ((skipSaturday && dayOfWeek === 6) || (skipSunday && dayOfWeek === 0));
            if (!isSkipped && currentDate >= startDate && currentDate <= endDate) {
                employeeLeaveDays.push({ date: new Date(currentDate), category, hours: leave.hoursPerDay });
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }
    }
    const MS_IN_HOUR = 1000 * 60 * 60;
    const rule = overtimeRules.find(r => r.assignedEmployeeIds.includes(employee.id));
    const weeklyRate = rule?.rates.find(r => r.weekly.enabled);
    const OT_THRESHOLD = weeklyRate ? weeklyRate.weekly.threshold : 40;
    const weeks: import('../shared/types.js').ReportWeek[] = [];
    let currentWeek = getWorkWeek(startDate, timeSettings.workweekStartsWith);
    while (currentWeek.start <= endDate) {
        const weekLogs = processedLogs.filter(log => log.startTime >= currentWeek.start.getTime() && log.startTime <= currentWeek.end.getTime());
        const weekLeaveDays = employeeLeaveDays.filter(l => l.date >= currentWeek.start && l.date <= currentWeek.end);
        if (weekLogs.length > 0 || weekLeaveDays.length > 0) {
            const weekPaidWorkHours = weekLogs
                .filter(log => {
                    const activity = activities.find(a => a.id === log.activityId);
                    return activity?.calculation !== ActivityCalculation.UNPAID;
                })
                .reduce((sum, log) => sum + (log.endTime! - log.startTime) / MS_IN_HOUR, 0);
            const weekPaidLeaveHours = weekLeaveDays
                .filter(leave => leave.category.calculation !== LeaveCategoryCalculation.UNPAID)
                .reduce((sum, leave) => sum + leave.hours, 0);
            const otEligibleHours = weekPaidWorkHours + (timeSettings.includePaidLeaveInOT ? weekPaidLeaveHours : 0);
            let overtimeToDistribute = 0;
            if (otEligibleHours > OT_THRESHOLD) {
                overtimeToDistribute = otEligibleHours - OT_THRESHOLD;
            }
            const allEntries = [
                ...weekLogs.map(log => ({ type: 'work' as const, data: log, time: log.startTime })),
                ...weekLeaveDays.map(leave => ({ type: 'leave' as const, data: leave, time: leave.date.getTime() }))
            ].sort((a, b) => a.time - b.time);
            const reportSegments: import('../shared/types.js').ReportSegment[] = allEntries.map(({ type, data }) => {
                if (type === 'work') {
                    const log = data as TimeLog;
                    const activity = activities.find(a => a.id === log.activityId);
                    const duration = (log.endTime! - log.startTime) / MS_IN_HOUR;
                    const isPaid = activity?.calculation !== ActivityCalculation.UNPAID;
                    const logDate = new Date(log.startTime);
                    return {
                        day: logDate.toLocaleDateString('en-US', { weekday: 'short' }),
                        date: `${logDate.getMonth() + 1}/${logDate.getDate()}`,
                        startTime: logDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                        stopTime: new Date(log.endTime!).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                        activity: activity?.name || 'Unknown',
                        unpaid: isPaid ? 0 : duration,
                        regular: isPaid ? duration : 0,
                        ot: 0,
                    };
                } else {
                    const leave = data as { date: Date; category: LeaveCategory; hours: number };
                    const isPaid = leave.category.calculation !== LeaveCategoryCalculation.UNPAID;
                    return {
                        day: leave.date.toLocaleDateString('en-US', { weekday: 'short' }),
                        date: `${leave.date.getMonth() + 1}/${leave.date.getDate()}`,
                        startTime: 'Leave', stopTime: '', activity: leave.category.name,
                        unpaid: isPaid ? 0 : leave.hours,
                        regular: isPaid ? leave.hours : 0,
                        ot: 0,
                    };
                }
            });
            for (let i = reportSegments.length - 1; i >= 0; i--) {
                if (overtimeToDistribute <= 0) break;
                const segment = reportSegments[i];
                if (segment.startTime !== 'Leave' && segment.regular > 0) {
                    const otForThisSegment = Math.min(segment.regular, overtimeToDistribute);
                    segment.ot += otForThisSegment;
                    segment.regular -= otForThisSegment;
                    overtimeToDistribute -= otForThisSegment;
                }
            }
            if (timeSettings.includePaidLeaveInOT && overtimeToDistribute > 0) {
                for (let i = reportSegments.length - 1; i >= 0; i--) {
                    if (overtimeToDistribute <= 0) break;
                    const segment = reportSegments[i];
                    if (segment.startTime === 'Leave' && segment.regular > 0) {
                        const otForThisSegment = Math.min(segment.regular, overtimeToDistribute);
                        segment.ot += otForThisSegment;
                        segment.regular -= otForThisSegment;
                        overtimeToDistribute -= otForThisSegment;
                    }
                }
            }
            const daysMap = new Map<string, import('../shared/types.js').ReportDay>();
            reportSegments.forEach(ps => {
                 const dayStart = new Date(ps.startTime === 'Invalid Date' || ps.startTime === 'Leave' ? new Date(`${ps.date}/${new Date().getFullYear()}`).setHours(0,0,0,0) : new Date(`${ps.date}/${new Date().getFullYear()} ${ps.startTime}`).setHours(0,0,0,0));
                const dayKey = dayStart.toLocaleDateString();
                if (!daysMap.has(dayKey)) daysMap.set(dayKey, { date: dayStart, segments: [], totalPaid: 0 });
                daysMap.get(dayKey)!.segments.push(ps);
            });
            daysMap.forEach(dayData => dayData.totalPaid = roundHours(dayData.segments.reduce((acc, s) => acc + s.regular + s.ot, 0), timeSettings.rounding));
            const days = Array.from(daysMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
            const weekTotalRegular = days.reduce((acc, d) => acc + d.segments.reduce((sAcc, s) => sAcc + s.regular, 0), 0);
            const weekTotalOT = days.reduce((acc, d) => acc + d.segments.reduce((sAcc, s) => sAcc + s.ot, 0), 0);
            weeks.push({
                weekEnding: currentWeek.end.toLocaleDateString(),
                days,
                totalRegular: roundHours(weekTotalRegular, timeSettings.rounding),
                totalOT: roundHours(weekTotalOT, timeSettings.rounding),
                totalPaid: roundHours(weekTotalRegular + weekTotalOT, timeSettings.rounding)
            });
        }
        const nextDate = new Date(currentWeek.start);
        nextDate.setDate(currentWeek.start.getDate() + 7);
        currentWeek = getWorkWeek(nextDate, timeSettings.workweekStartsWith);
        if (currentWeek.end > endDate) currentWeek.end = endDate;
    }
    const unroundedGrandTotals = weeks.reduce((acc, w) => { acc.regular += w.totalRegular; acc.ot += w.totalOT; acc.paid += w.totalPaid; return acc; }, { regular: 0, ot: 0, paid: 0 });
    const grandTotals = { regular: roundHours(unroundedGrandTotals.regular, timeSettings.rounding), ot: roundHours(unroundedGrandTotals.ot, timeSettings.rounding), paid: roundHours(unroundedGrandTotals.paid, timeSettings.rounding) };
    const activitySummaryUnrounded: Record<string, import('../shared/types.js').ActivitySummary> = {};
    const leaveSummaryUnrounded: Record<string, import('../shared/types.js').ActivitySummary> = {};
    weeks.forEach(w => w.days.forEach(d => d.segments.forEach(s => {
        const summaryTarget = s.startTime === 'Leave' ? leaveSummaryUnrounded : activitySummaryUnrounded;
        if (!summaryTarget[s.activity]) summaryTarget[s.activity] = { unpaid: 0, regular: 0, ot: 0, paid: 0 };
        const summary = summaryTarget[s.activity];
        summary.unpaid += s.unpaid;
        summary.regular += s.regular;
        summary.ot += s.ot;
        summary.paid += s.regular + s.ot;
    })));
    const activitySummary: Record<string, import('../shared/types.js').ActivitySummary> = {};
    for (const key in activitySummaryUnrounded) {
        const unrounded = activitySummaryUnrounded[key];
        activitySummary[key] = { unpaid: roundHours(unrounded.unpaid, timeSettings.rounding), regular: roundHours(unrounded.regular, timeSettings.rounding), ot: roundHours(unrounded.ot, timeSettings.rounding), paid: roundHours(unrounded.paid, timeSettings.rounding) };
    }
    const leaveSummary: Record<string, import('../shared/types.js').ActivitySummary> = {};
    for (const key in leaveSummaryUnrounded) {
        const unrounded = leaveSummaryUnrounded[key];
        leaveSummary[key] = { unpaid: roundHours(unrounded.unpaid, timeSettings.rounding), regular: roundHours(unrounded.regular, timeSettings.rounding), ot: roundHours(unrounded.ot, timeSettings.rounding), paid: roundHours(unrounded.paid, timeSettings.rounding) };
    }
    return { employeeNumber: employee.emp_number, employeeName: `${employee.lastName}, ${employee.firstName}`, dateRange: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`, generatedAt: new Date().toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }), weeks, grandTotals, activitySummary, leaveSummary };
}

export function convertTimeLogToEditableSegment(
    log: TimeLog,
    employee: Employee,
    activity: Activity
): EditableTimeSegment {
    const start = new Date(log.startTime);
    const stop = log.endTime ? new Date(log.endTime) : null;
    return {
        id: log.id,
        timeLogId: log.id,
        source: 'time',
        employeeId: employee.id,
        employeeName: formatEmployeeName(employee, NameDisplayFormat.LAST_FIRST),
        activity: activity.name,
        activityId: activity.id,
        startTime: log.startTime,
        endTime: log.endTime,
        startDate: start.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric' }).replace(',', ''),
        ui_startTime: start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
        stopDate: stop ? stop.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric' }).replace(',', '') : 'In Progress',
        ui_stopTime: stop ? stop.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '',
        hours: stop ? (log.endTime! - log.startTime) / (3600 * 1000) : (Date.now() - log.startTime) / (3600 * 1000),
    };
}

export function convertTimeLogsToEditableSegments(
    timeLogs: TimeLog[],
    leaveEntries: LeaveEntry[],
    activities: Activity[],
    leaveCategories: LeaveCategory[],
    employees: Employee[],
    filterStartTime?: number,
    filterEndTime?: number
): EditableTimeSegment[] {
    const allFinalSegments: EditableTimeSegment[] = [];
    const activityMap = new Map(activities.map(a => [a.id, a]));
    const employeeByEmpNumberMap = new Map(employees.map(e => [e.emp_number, e]));
    const employeeByIdMap = new Map(employees.map(e => [e.id, e]));
    const leaveCategoryMap = new Map(leaveCategories.map(c => [c.id, c]));
    for (const log of timeLogs) {
        if (filterStartTime && log.endTime && log.endTime < filterStartTime) continue;
        if (filterEndTime && log.startTime > filterEndTime) continue;
        const employee = employeeByEmpNumberMap.get(log.employeeId);
        if (!employee) continue;
        const activity = activityMap.get(log.activityId);
        if (!activity) continue;
        const start = new Date(log.startTime);
        const stop = log.endTime ? new Date(log.endTime) : null;
        allFinalSegments.push({
            id: log.id,
            timeLogId: log.id,
            source: 'time',
            employeeId: employee.id,
            employeeName: formatEmployeeName(employee, NameDisplayFormat.LAST_FIRST),
            activity: activity.name,
            activityId: activity.id,
            startTime: log.startTime,
            endTime: log.endTime,
            startDate: start.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric' }).replace(',', ''),
            ui_startTime: start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
            stopDate: stop ? stop.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric' }).replace(',', '') : 'In Progress',
            ui_stopTime: stop ? stop.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '',
            hours: stop ? (log.endTime! - log.startTime) / (3600 * 1000) : (Date.now() - log.startTime) / (3600 * 1000),
        });
    }
    for (const leave of leaveEntries) {
        const category = leaveCategoryMap.get(leave.leaveCategoryId);
        if (!category) continue;
        const loopStartDate = filterStartTime && filterStartTime > leave.startDate ? filterStartTime : leave.startDate;
        const loopEndDate = filterEndTime && filterEndTime < leave.endDate ? filterEndTime : leave.endDate;
        let currentDate = new Date(loopStartDate);
        currentDate.setHours(0, 0, 0, 0);
        const endDate = new Date(loopEndDate);
        while (currentDate.getTime() <= endDate.getTime()) {
            const dayOfWeek = currentDate.getDay();
            const isOrangeHRMLeave = leave.id.startsWith('ohrm_');
            const skipSaturday = leave.skipSaturday ?? true;
            const skipSunday = leave.skipSunday ?? true;
            const isSkipped = !isOrangeHRMLeave && ((skipSaturday && dayOfWeek === 6) || (skipSunday && dayOfWeek === 0));
            if (!isSkipped) {
                const dayString = toDateInputString(currentDate);
                for (const empId of leave.employeeIds) {
                    const employee = employeeByIdMap.get(empId);
                    if (!employee) continue;
                    allFinalSegments.push({
                        id: `leave_${leave.id}_${empId}_${dayString}`,
                        leaveEntryId: leave.id,
                        source: 'leave',
                        employeeId: empId,
                        employeeName: formatEmployeeName(employee, NameDisplayFormat.LAST_FIRST),
                        activity: category.name,
                        activityId: category.id,
                        startTime: currentDate.getTime(),
                        hours: leave.hoursPerDay,
                        startDate: currentDate.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric' }).replace(',', ''),
                        ui_startTime: '',
                        stopDate: '',
                        ui_stopTime: '',
                    });
                }
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }
    }
    return allFinalSegments.sort((a, b) => b.startTime - a.startTime);
}

function escapeCsvCell(cell: any): string {
    if (cell === null || cell === undefined) {
        return '';
    }
    const str = String(cell);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

export function exportToCsv(data: any[], filename: string) {
    if (!data || data.length === 0) {
        alert('No data to export.');
        return;
    }
    const headers = Object.keys(data[0]);
    const csvRows = [
        headers.join(','),
        ...data.map(row =>
            headers.map(fieldName => escapeCsvCell(row[fieldName])).join(',')
        )
    ];
    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function parseCsv<T>(csvText: string): T[] {
    const lines = csvText.trim().split(/\r\n|\n/);
    if (lines.length < 2) return [];
    const csvSplitRegex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
    const headers = lines[0].split(csvSplitRegex).map(h => h.trim().replace(/^"|"$/g, ''));
    const data: T[] = [];
    for (let i = 1; i < lines.length; i++) {
        if (!lines[i]) continue;
        const values = lines[i].split(csvSplitRegex);
        if (values.length !== headers.length) continue; 
        const entry: any = {};
        for (let j = 0; j < headers.length; j++) {
            let value = values[j] || '';
            if (value.startsWith('"') && value.endsWith('"')) {
                value = value.substring(1, value.length - 1).replace(/""/g, '"');
            }
            entry[headers[j]] = value;
        }
        data.push(entry as T);
    }
    return data;
}

export function generateTimecardSummaryData(
    employees: Employee[],
    timeLogs: TimeLog[],
    leaveEntries: LeaveEntry[],
    leaveCategories: LeaveCategory[],
    activities: Activity[],
    startDate: Date,
    endDate: Date,
    overtimeRules: OvertimeRule[],
    timeSettings: TimeSettings
): TimecardSummaryData {
    const employeeSummaries: EmployeeSummary[] = [];
    for (const employee of employees) {
        const detailData = generateTimecardDetailData(employee, timeLogs, leaveEntries, leaveCategories, activities, startDate, endDate, overtimeRules, timeSettings);
        const unpaidActivities = Object.values(detailData.activitySummary).reduce((sum, s) => sum + s.unpaid, 0);
        const unpaidLeave = Object.values(detailData.leaveSummary).reduce((sum, s) => sum + s.unpaid, 0);
        const totalUnpaid = unpaidActivities + unpaidLeave;
        employeeSummaries.push({
            employeeId: employee.id,
            employeeNumber: employee.emp_number,
            employeeName: detailData.employeeName,
            unpaid: totalUnpaid,
            regular: detailData.grandTotals.regular,
            ot: detailData.grandTotals.ot,
            totalPaid: detailData.grandTotals.paid
        });
    }
    const totals = employeeSummaries.reduce((acc, summary) => {
        acc.unpaid += summary.unpaid;
        acc.regular += summary.regular;
        acc.ot += summary.ot;
        acc.totalPaid += summary.totalPaid;
        return acc;
    }, { unpaid: 0, regular: 0, ot: 0, totalPaid: 0 });
    return {
        dateRange: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
        generatedAt: new Date().toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }),
        employeeSummaries,
        totals
    };
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}